// Kartlag «Bygninger (3D)» — OSM-bygningsekstrudering i MapLibre + HØYDE-OPPSLAG.
//
// HVORFOR (ikke bare pynt): Terrarium-DEM-en vi bruker til 3D-terreng og
// siktelinjer er en DTM — bakken UTEN bygninger. I bebyggelse lyver derfor en
// siktelinje som bare bruker terreng: den ser rett gjennom en boligblokk. Dette
// laget gir siktelinje-/dekningsmodulene bygningshøyden de må legge OPPÅ
// terrenghøyden (`bygningshoydeAt`, `bygningerLangs`).
//
// KILDE: OpenFreeMap «planet» (nøkkelfrie OpenMapTiles-vektorfliser).
//   TileJSON: https://tiles.openfreemap.org/planet  → lag `building` med feltene
//   `render_height`, `render_min_height`, `hide_3d`, `colour` (z13–z14).
//   Flis-URL-en er VERSJONERT (…/planet/<versjon>/{z}/{x}/{y}.pbf) og byttes ved
//   hver planet-oppdatering. Den uversjonerte stien svarer HTTP 200 med TOM
//   kropp — derfor bruker vi `url` (TileJSON), aldri hardkodet `tiles`.
//
// ⚠ ÆRLIG OM DATAKVALITETEN (verifisert mot fliser + Overpass 2026-07-27):
//   · Bygnings-GEOMETRIEN er komplett i Norge. Flisene slår sammen nabobygg med
//     samme høyde til ÉN multipolygon-feature (Grünerløkka z14: 45 features =
//     1630 ringer ≈ 1462 OSM-bygg). Lavt feature-tall betyr IKKE dårlig dekning.
//   · `render_height` er ALLTID satt (0 % manglende felt), MEN verdien er
//     `height`-tag, ellers `building:levels × ~3,7 m`, ellers OpenMapTiles'
//     STANDARDVERDI 5 m. Andelen 5 m-standard i målte fliser:
//       Grünerløkka 35 % · Oslo Barcode 23 % · Røa (villa) 88 %
//       Larvik 82 % · Råde (bygd) 100 %
//     ⇒ I tettbygde bykjerner er høydene stort sett reelle; i norsk forstad/bygd
//     er de FLESTE bygningshøyder en fiksjon på 5 m. Oppslaget flagger dette
//     (`erStandardhoyde`) så siktelinje-UI kan si det til operatøren i stedet for
//     å påstå presisjon som ikke finnes.
//
// KOSTNAD: vektorfliser er båndbreddetunge (z14-fliser over Oslo: 100–330 kB
// hver, alle lag). Derfor: AV som standard, kilden legges til FØRST når laget
// eller oppslaget aktiveres, og `minzoom` 14 (bygninger under z14 er både
// meningsløst visuelt og en flisregn). Faller kilden bort (gradert nett/ingen
// egress) går status til «nede» og UI-et SKAL vise det — ingen tom, blank løgn.
import type {
  Map as MlMap, MapGeoJSONFeature, VectorSourceSpecification,
  FillExtrusionLayerSpecification, FillLayerSpecification,
} from 'maplibre-gl';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { point } from '@turf/helpers';

/** Kilde-id i stilen (vektorfliser fra OpenFreeMap). */
export const BYGNING_KILDE = 'openfreemap';
/** Kildelaget i OpenMapTiles-skjemaet. */
export const BYGNING_KILDELAG = 'building';
/** Synlig 3D-ekstrudering. */
export const BYGNING_LAG = 'bygninger-3d';
/** Usynlig, men søkbart flatelag — gjør høyde-oppslaget mulig UTEN at 3D vises.
 *  `fill-opacity: 0` skjuler laget visuelt; `visibility: 'none'` ville i tillegg
 *  gjort det usøkbart for `queryRenderedFeatures`. Det er forskjellen vi utnytter. */
export const BYGNING_OPPSLAG_LAG = 'bygninger-oppslag';

/** TileJSON-endepunkt (ikke flis-mønster — se filhodet). */
export const OFM_TILEJSON = 'https://tiles.openfreemap.org/planet';

/** ODbL KREVER attribusjon av OSM-data. Samme form som de andre kildene. */
export const OFM_ATTRIBUSJON =
  '© OpenStreetMap-bidragsytere (ODbL) · © OpenMapTiles · OpenFreeMap';

/** Kildelaget finnes fra z13, men vi tegner/leser først fra z14 (se KOSTNAD). */
export const BYGNING_MIN_ZOOM = 14;
/** Kildens egen maxzoom — MapLibre overzoomer over dette (fliser gjenbrukes). */
export const BYGNING_KILDE_MAXZOOM = 14;

/** OpenMapTiles' fallback-høyde når OSM mangler `height` OG `building:levels`.
 *  Treff på nøyaktig denne verdien er derfor MISTENKELIG, ikke en måling. */
export const STANDARD_HOYDE_M = 5;

/** Hva ett bygningstreff faktisk sier. */
export interface BygningTreff {
  /** Topphøyde over bakken (m) — `render_height`. */
  hoydeM: number;
  /** Underkant over bakken (m) — `render_min_height` (broer/utstikk). */
  basisM: number;
  /** true = høyden er OpenMapTiles' standard 5 m, altså ikke målt/tagget. */
  erStandardhoyde: boolean;
  /** OSM-taggen `hide_3d` — bygget skal IKKE ekstruderes (f.eks. underbygg). */
  skjult3d: boolean;
}

/** Bygningstreff kombinert med terrenghøyde — det siktelinjer trenger. */
export interface BygningHoyde extends BygningTreff {
  /** Terrenghøyde (m o.h.) fra DEM-en, korrigert for terreng-overdrivelse.
   *  null når 3D-terreng er av eller DEM-flisen ikke er lastet. */
  terrengM: number | null;
  /** Bygningstopp i m o.h. = terrengM + hoydeM. null når terrengM er null. */
  toppAmslM: number | null;
}

/** Ett punkt langs en profil (siktelinje) med bygningen som står der. */
export interface LangsPunkt {
  lon: number;
  lat: number;
  /** Avstand fra startpunktet (m). */
  avstandM: number;
  /** Bygningen punktet ligger inne i, eller null. */
  bygning: BygningHoyde | null;
}

/** Ærlig laststatus — UI-et viser denne ordrett, jf. UX_DESIGNSYSTEM §10. */
export type BygningStatus = 'av' | 'venter-zoom' | 'laster' | 'ok' | 'nede';

export const BYGNING_STATUS_TEKST: Record<BygningStatus, string> = {
  av: 'Bygninger (3D) er av',
  'venter-zoom': `Zoom inn til nivå ${BYGNING_MIN_ZOOM} for å se bygninger`,
  laster: 'Henter bygningsfliser (OpenFreeMap) …',
  ok: 'Bygninger lastet (OpenStreetMap via OpenFreeMap)',
  nede: 'Bygningskilden svarer ikke — 3D-bygg og høyde-oppslag utilgjengelig',
};

// ── Rene spesifikasjoner (testbare uten kart) ─────────────────────────────

/** Vektorkilden. `url` (TileJSON) — IKKE `tiles`, se filhodet. */
export function bygningKildeSpec(tilejson = OFM_TILEJSON): VectorSourceSpecification {
  return {
    type: 'vector',
    url: tilejson,
    attribution: OFM_ATTRIBUSJON,
  };
}

/** Fargetrapp for ekstruderingen. Verdiene speiler theme.css-tokenene
 *  (--surface-elevated → --faintest → --faint): MapLibre kan ikke lese
 *  CSS-variabler, så de er skrevet ut — samme praksis som de andre kartlagene. */
const BYGNING_FARGE: [number, string][] = [
  [0, '#151916'], // --surface-elevated (lavt/ukjent)
  [5, '#1f2a24'],
  [15, '#2d3e28'], // --olive
  [40, '#3b4b3b'], // --faintest
  [80, '#4b5b4b'], // --faint (høyhus skiller seg ut)
];

/** Den synlige 3D-ekstruderingen. Respekterer `hide_3d` via filter. */
export function bygningLagSpec(): FillExtrusionLayerSpecification {
  const trapp: (number | string)[] = [];
  for (const [h, c] of BYGNING_FARGE) trapp.push(h, c);
  return {
    id: BYGNING_LAG,
    type: 'fill-extrusion',
    source: BYGNING_KILDE,
    'source-layer': BYGNING_KILDELAG,
    minzoom: BYGNING_MIN_ZOOM,
    // `hide_3d` = OSM sier «ikke ekstruder» (delbygg/underbygg som ville dublert).
    filter: ['!=', ['get', 'hide_3d'], true],
    layout: { visibility: 'none' }, // AV som standard (båndbredde)
    paint: {
      'fill-extrusion-color': [
        'interpolate', ['linear'],
        ['coalesce', ['get', 'render_height'], STANDARD_HOYDE_M],
        ...trapp,
      ] as never,
      'fill-extrusion-height': ['coalesce', ['get', 'render_height'], STANDARD_HOYDE_M],
      'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], 0],
      // Litt gjennomsiktig: operatøren må fortsatt se veier/symboler under.
      'fill-extrusion-opacity': 0.82,
      'fill-extrusion-vertical-gradient': true,
    },
  };
}

/** Det usynlige oppslags-laget (fill-opacity 0 ⇒ søkbart, men ikke synlig).
 *  Inkluderer OGSÅ `hide_3d`-bygg: de finnes fysisk og sperrer sikt selv om de
 *  ikke skal ekstruderes visuelt. */
export function bygningOppslagLagSpec(): FillLayerSpecification {
  return {
    id: BYGNING_OPPSLAG_LAG,
    type: 'fill',
    source: BYGNING_KILDE,
    'source-layer': BYGNING_KILDELAG,
    minzoom: BYGNING_MIN_ZOOM,
    paint: { 'fill-color': '#000000', 'fill-opacity': 0 },
  };
}

// ── Ren logikk: tolkning og utvelgelse av høyde ───────────────────────────

/** Tolker rå flis-egenskaper til et bygningstreff. Returnerer null når det ikke
 *  finnes noen brukbar høyde i det hele tatt. */
export function normalisertHoyde(props: Record<string, unknown> | undefined | null): BygningTreff | null {
  if (!props) return null;
  const rå = props.render_height;
  const h = typeof rå === 'number' && Number.isFinite(rå) ? rå : null;
  const råBasis = props.render_min_height;
  const basis = typeof råBasis === 'number' && Number.isFinite(råBasis) ? Math.max(0, råBasis) : 0;
  // Mangler feltet, eller er 0/negativ: bruk standardhøyden, men FLAGG den.
  const hoydeM = h !== null && h > 0 ? h : STANDARD_HOYDE_M;
  const erStandardhoyde = !(h !== null && h > 0) || h === STANDARD_HOYDE_M;
  return {
    hoydeM,
    basisM: Math.min(basis, hoydeM),
    erStandardhoyde,
    skjult3d: props.hide_3d === true,
  };
}

/** Velger det HØYESTE av flere overlappende bygningstreff (podium + tårn på
 *  samme punkt ⇒ tårnet er det som sperrer sikten). */
export function velgHoyeste(treff: readonly (BygningTreff | null)[]): BygningTreff | null {
  let best: BygningTreff | null = null;
  for (const t of treff) {
    if (!t) continue;
    if (!best || t.hoydeM > best.hoydeM) best = t;
  }
  return best;
}

/** `queryTerrainElevation` returnerer høyde MULTIPLISERT med terreng-
 *  overdrivelsen. Deler den bort så vi får ekte meter over havet. */
export function ekteTerrengM(raa: number | null | undefined, overdrivelse: number): number | null {
  if (raa === null || raa === undefined || !Number.isFinite(raa)) return null;
  const e = Number.isFinite(overdrivelse) && overdrivelse > 0 ? overdrivelse : 1;
  return raa / e;
}

const JORD_R = 6_371_008.8; // m — samme middelradius som @turf

/** Storsirkelavstand i meter (haversine). Egen, liten, for å slippe å dra inn
 *  @turf/distance i en varm løkke over hundrevis av profilpunkter. */
export function avstandM(a: readonly [number, number], b: readonly [number, number]): number {
  const [lon1, lat1] = a;
  const [lon2, lat2] = b;
  const f1 = (lat1 * Math.PI) / 180;
  const f2 = (lat2 * Math.PI) / 180;
  const dF = f2 - f1;
  const dL = ((lon2 - lon1) * Math.PI) / 180;
  const s = Math.sin(dF / 2) ** 2 + Math.cos(f1) * Math.cos(f2) * Math.sin(dL / 2) ** 2;
  return 2 * JORD_R * Math.asin(Math.min(1, Math.sqrt(s)));
}

export interface ProfilOpts {
  /** Ønsket avstand mellom prøvepunkter (m). Default 20 m (typisk husbredde). */
  stegM?: number;
  /** Hardt tak på antall punkter (ytelse). Default 400. */
  maksPunkter?: number;
}

/** Prøvepunkter langs en rett linje fra→til, inkludert begge endepunkt.
 *  Lineær interpolasjon i lon/lat: over noen få km er avviket fra storsirkelen
 *  under en meter, og siktelinjer i bebyggelse er kortere enn det. */
export function profilPunkter(
  fra: readonly [number, number],
  til: readonly [number, number],
  opts: ProfilOpts = {},
): { lon: number; lat: number; avstandM: number }[] {
  const steg = Math.max(1, opts.stegM ?? 20);
  const maks = Math.max(2, Math.floor(opts.maksPunkter ?? 400));
  const total = avstandM(fra, til);
  if (!(total > 0)) return [{ lon: fra[0], lat: fra[1], avstandM: 0 }];
  const n = Math.min(maks, Math.max(2, Math.ceil(total / steg) + 1));
  const ut: { lon: number; lat: number; avstandM: number }[] = [];
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    ut.push({
      lon: fra[0] + (til[0] - fra[0]) * t,
      lat: fra[1] + (til[1] - fra[1]) * t,
      avstandM: total * t,
    });
  }
  return ut;
}

/** Punkt-i-polygon mot en GeoJSON-geometri fra en vektorflis (Polygon eller
 *  MultiPolygon — flisene slår sammen nabobygg, så MultiPolygon er normalen). */
export function punktIGeometri(lon: number, lat: number, geom: unknown): boolean {
  const g = geom as { type?: string } | null;
  if (!g || (g.type !== 'Polygon' && g.type !== 'MultiPolygon')) return false;
  try {
    return booleanPointInPolygon(point([lon, lat]), g as never);
  } catch {
    return false; // ødelagt geometri i flisen skal ikke velte oppslaget
  }
}

// ── Kartlaget ─────────────────────────────────────────────────────────────

export interface BygningerOpts {
  /** Alternativt TileJSON-endepunkt (test / egen flisserver i KO-noden). */
  tilejson?: string;
}

export class BygningerLag {
  private lagtTil = false;   // kilde + oppslagslag finnes i stilen
  private ekstrudert = false; // 3D-laget finnes i stilen
  private on = false;
  private state: BygningStatus = 'av';
  private readonly tilejson: string;

  /** Kalles ved hver tilstands-/statusendring (dock-knapp + statuslinje). */
  onStateChange?: () => void;

  constructor(private map: MlMap, opts: BygningerOpts = {}) {
    this.tilejson = opts.tilejson ?? OFM_TILEJSON;
    // Ærlig status: kilden kan feile (gradert nett / ingen egress).
    this.map.on?.('error', (e: unknown) => {
      const id = (e as { sourceId?: string } | undefined)?.sourceId;
      if (id === BYGNING_KILDE) this.settStatus('nede');
    });
    this.map.on?.('sourcedata', (e: unknown) => {
      const d = e as { sourceId?: string; isSourceLoaded?: boolean } | undefined;
      if (d?.sourceId !== BYGNING_KILDE) return;
      if (d.isSourceLoaded && this.state !== 'nede') this.oppdaterStatus();
    });
    this.map.on?.('zoomend', () => { if (this.on) this.oppdaterStatus(); });
  }

  // ── Oppsett (lat: koster først når noen faktisk trenger bygninger) ──────

  /** Legger til vektorkilden + det USYNLIGE oppslagslaget. Nok for
   *  `bygningshoydeAt`/`bygningerLangs` — 3D tegnes ikke.
   *  Siktelinje-modulen kaller denne, ikke `settPaa`. */
  aktiverOppslag(): boolean {
    if (this.lagtTil) return true;
    try {
      if (!this.map.getSource?.(BYGNING_KILDE)) {
        this.map.addSource(BYGNING_KILDE, {
          ...bygningKildeSpec(this.tilejson),
        } as never);
      }
      if (!this.map.getLayer?.(BYGNING_OPPSLAG_LAG)) {
        this.map.addLayer(bygningOppslagLagSpec() as never, this.forsteSymbolLag());
      }
      this.lagtTil = true;
      this.settStatus('laster');
      return true;
    } catch {
      // Stilen er ikke lastet ennå — prøv igjen ved neste kall.
      return false;
    }
  }

  /** Første symbol-lag i stilen. 3D-bygg og oppslagslaget legges FØR dette, så
   *  militærsymboler/etiketter alltid tegnes OPPÅ bygningene. */
  private forsteSymbolLag(): string | undefined {
    try {
      const lag = this.map.getStyle?.()?.layers ?? [];
      return lag.find((l) => l.type === 'symbol')?.id;
    } catch {
      return undefined;
    }
  }

  private leggTilEkstrudering(): boolean {
    if (this.ekstrudert) return true;
    if (!this.aktiverOppslag()) return false;
    try {
      if (!this.map.getLayer?.(BYGNING_LAG)) {
        this.map.addLayer(bygningLagSpec() as never, this.forsteSymbolLag());
      }
      this.ekstrudert = true;
      return true;
    } catch {
      return false;
    }
  }

  // ── Av/på ──────────────────────────────────────────────────────────────

  settPaa(on: boolean): boolean {
    if (on && !this.leggTilEkstrudering()) return this.on;
    this.on = on;
    try {
      this.map.setLayoutProperty?.(BYGNING_LAG, 'visibility', on ? 'visible' : 'none');
    } catch { /* laget ennå ikke lagt til */ }
    this.oppdaterStatus();
    return this.on;
  }
  veksle(): boolean { return this.settPaa(!this.on); }
  paa(): boolean { return this.on; }

  // ── Status ─────────────────────────────────────────────────────────────

  private settStatus(s: BygningStatus) {
    if (this.state === s) return;
    this.state = s;
    this.onStateChange?.();
  }

  private oppdaterStatus() {
    if (this.state === 'nede') return; // nede overstyrer til kilden svarer igjen
    if (!this.on && !this.lagtTil) { this.settStatus('av'); return; }
    if (!this.on) { this.settStatus('av'); return; }
    const z = this.map.getZoom?.() ?? 0;
    if (z < BYGNING_MIN_ZOOM) { this.settStatus('venter-zoom'); return; }
    this.settStatus(this.lastetBygninger() > 0 ? 'ok' : 'laster');
  }

  status(): BygningStatus { return this.state; }
  statusTekst(): string { return BYGNING_STATUS_TEKST[this.state]; }

  /** Antall bygnings-features i lastede fliser. MERK: flisene slår sammen
   *  nabobygg med samme høyde, så dette er features, IKKE antall bygg. */
  lastetBygninger(): number {
    try {
      return this.map.querySourceFeatures?.(BYGNING_KILDE, { sourceLayer: BYGNING_KILDELAG })?.length ?? 0;
    } catch {
      return 0;
    }
  }

  // ── HØYDE-OPPSLAG (hovedpoenget) ───────────────────────────────────────

  /** Gjeldende terreng-overdrivelse (1 når 3D-terreng er av). */
  private overdrivelse(): number {
    const t = this.map.getTerrain?.();
    const e = t?.exaggeration;
    return typeof e === 'number' && e > 0 ? e : 1;
  }

  /** Terrenghøyde i EKTE meter over havet på et punkt (null uten 3D-terreng
   *  eller uten lastet DEM-flis). */
  terrengAt(lon: number, lat: number): number | null {
    try {
      return ekteTerrengM(this.map.queryTerrainElevation?.([lon, lat]) ?? null, this.overdrivelse());
    } catch {
      return null;
    }
  }

  /**
   * Bygningshøyde i et punkt.
   *
   * ⚠ REELL BEGRENSNING (MapLibre, ikke noe vi kan skjule): oppslaget ser BARE
   * vektorfliser som er LASTET. I praksis betyr det
   *   · zoom ≥ {@link BYGNING_MIN_ZOOM} (14), og
   *   · punktet i/nær gjeldende kartutsnitt (MapLibre beholder noen fliser rundt).
   * Utenfor det returneres null — som IKKE betyr «ingen bygning der», bare
   * «ukjent». Siktelinje-UI må si «ukjent», aldri «fri sikt».
   *
   * Kall {@link aktiverOppslag} (eller slå på laget) minst én gang først.
   */
  bygningshoydeAt(lon: number, lat: number): BygningHoyde | null {
    this.aktiverOppslag();
    const treff = velgHoyeste([
      ...this.treffRendret(lon, lat),
      ...this.treffKilde(lon, lat),
    ]);
    if (!treff) return null;
    const terrengM = this.terrengAt(lon, lat);
    return {
      ...treff,
      terrengM,
      toppAmslM: terrengM === null ? null : terrengM + treff.hoydeM,
    };
  }

  /** Rask vei: piksel-treff i det usynlige oppslagslaget (kun innenfor lerretet). */
  private treffRendret(lon: number, lat: number): (BygningTreff | null)[] {
    try {
      const p = this.map.project?.([lon, lat]);
      if (!p) return [];
      const feats = this.map.queryRenderedFeatures?.(p as never, {
        layers: [BYGNING_OPPSLAG_LAG],
      }) as MapGeoJSONFeature[] | undefined;
      return (feats ?? []).map((f) => normalisertHoyde(f.properties as Record<string, unknown>));
    } catch {
      return [];
    }
  }

  /** Robust vei: alle bygg i LASTEDE fliser + eget punkt-i-polygon. Fanger
   *  punkter litt utenfor lerretet (MapLibre holder nabofliser i minne). */
  private treffKilde(lon: number, lat: number): (BygningTreff | null)[] {
    try {
      const feats = this.map.querySourceFeatures?.(BYGNING_KILDE, {
        sourceLayer: BYGNING_KILDELAG,
      }) as { geometry?: unknown; properties?: Record<string, unknown> }[] | undefined;
      if (!feats?.length) return [];
      const ut: (BygningTreff | null)[] = [];
      for (const f of feats) {
        if (!punktIGeometri(lon, lat, f.geometry)) continue;
        ut.push(normalisertHoyde(f.properties));
      }
      return ut;
    } catch {
      return [];
    }
  }

  /**
   * Bygninger langs en siktelinje fra→til. Ett prøvepunkt per {@link ProfilOpts.stegM}
   * meter; `bygning` er null der det ikke står bygg (eller der flisen ikke er
   * lastet — se {@link bygningshoydeAt}).
   */
  bygningerLangs(
    fra: readonly [number, number],
    til: readonly [number, number],
    opts: ProfilOpts = {},
  ): LangsPunkt[] {
    this.aktiverOppslag();
    return profilPunkter(fra, til, opts).map((p) => ({
      lon: p.lon,
      lat: p.lat,
      avstandM: p.avstandM,
      bygning: this.bygningshoydeAt(p.lon, p.lat),
    }));
  }

  /** Høyeste bygningstopp (m o.h.) langs en linje — det ene tallet en
   *  siktelinje-sjekk trenger. null når ingen bygning/terreng er kjent. */
  hoyesteHindringAmsl(
    fra: readonly [number, number],
    til: readonly [number, number],
    opts: ProfilOpts = {},
  ): { amslM: number; ved: LangsPunkt } | null {
    let best: { amslM: number; ved: LangsPunkt } | null = null;
    for (const p of this.bygningerLangs(fra, til, opts)) {
      const a = p.bygning?.toppAmslM;
      if (a === null || a === undefined) continue;
      if (!best || a > best.amslM) best = { amslM: a, ved: p };
    }
    return best;
  }
}

// ── Én instans per kart ───────────────────────────────────────────────────
// Både bakgrunnskart-kontrollen (basemaps.ts), dock-knappen i main.ts og
// siktelinje-modulen skal snakke med SAMME lag. Registeret gjør at ingen av dem
// trenger å eie instansen — de ber bare om laget for kartet sitt.
const REGISTER = new WeakMap<MlMap, BygningerLag>();

/** Henter (eller oppretter) bygningslaget for et kart. */
export function bygningerFor(map: MlMap, opts?: BygningerOpts): BygningerLag {
  let l = REGISTER.get(map);
  if (!l) {
    l = new BygningerLag(map, opts);
    REGISTER.set(map, l);
  }
  return l;
}
